import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAppDataContext } from '../../../contexts/appData';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import {
  areTranslationRecordsEqual,
  Language,
  TranslationRecord,
} from '../../../types/language';
import { SimplePort } from '../../../types/port';
import IslandForm, { IslandFormInputValue } from './IslandForm';

const EditIslandPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { islands, refetchIslandAndPortData, isFetching } = useAppDataContext();

  const { updateIsland, updatePort, deletePort, createPort } = useIotsApi();

  const { islandId: islandIdQueryParam } = useParams();

  const island = useMemo(() => {
    const islandId =
      islandIdQueryParam && islandIdQueryParam.toLocaleLowerCase() !== 'create'
        ? islandIdQueryParam
        : null;
    if (!islandId) {
      return null;
    }
    return islands.find(({ id }) => id === islandId);
  }, [islandIdQueryParam, islands]);

  useEffect(() => {
    if (!island && !isFetching) {
      void navigate('/admin/islands');
    }
  }, [isFetching, island, navigate]);

  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: ({
      updatedIsland,
      updatedPorts,
      deletedPorts,
      newPorts,
    }: {
      updatedIsland: TranslationRecord | null;
      updatedPorts: SimplePort[];
      deletedPorts: string[];
      newPorts: TranslationRecord[];
    }) =>
      Promise.allSettled([
        updatedIsland
          ? updateIsland(island?.id ?? '', { names: updatedIsland })
          : Promise.resolve(),
        ...updatedPorts.map(port => updatePort(port.id, { names: port.names })),
        ...deletedPorts.map(portId => deletePort(portId)),
        ...newPorts.map(port =>
          createPort({ names: port, islandId: island?.id ?? '' }),
        ),
      ]).then(settledPromises => {
        // If at least a operation succeeded, it is treated as a success and we'll see the outcome
        const failedOperationsCount = settledPromises.filter(
          operation => operation.status === 'rejected',
        ).length;
        if (failedOperationsCount < settledPromises.length) {
          return failedOperationsCount === 0;
        }
        throw new Error('All operations failed');
      }),
  });

  const initialValue = useMemo(() => {
    if (!island) {
      return null;
    }

    const initialValue: IslandFormInputValue = {
      names: island.names,
      ports: [],
    };

    island.ports.forEach(port => {
      if (!port.isActive) {
        return;
      }
      initialValue.ports.push({ id: port.id, names: port.names });
      initialValue.ports.sort((a, b) =>
        a.names[Language.EN].localeCompare(b.names[Language.EN]),
      );
    });

    return initialValue;
  }, [island]);

  return (
    <IslandForm
      initialValue={initialValue}
      onSubmit={(value, callback) => {
        if (!initialValue) {
          return;
        }
        const updatedIsland = areTranslationRecordsEqual(
          value.names,
          initialValue.names,
        )
          ? null
          : value.names;

        const initialPortsMap = initialValue.ports.reduce(
          (acc, port) => {
            acc[port.id] = port.names;
            return acc;
          },
          {} as Record<string, TranslationRecord>,
        );

        const updatedPorts: SimplePort[] = [];
        const newPorts: TranslationRecord[] = [];
        const remainingPortIdsSet = new Set<string>();

        value.ports.forEach(port => {
          const { id: portId } = port;

          if (portId) {
            remainingPortIdsSet.add(portId);

            const initialPortNames = initialPortsMap[portId];
            if (
              initialPortNames &&
              !areTranslationRecordsEqual(port.names, initialPortNames)
            ) {
              updatedPorts.push(port as SimplePort);
            }
          } else {
            newPorts.push(port.names);
          }
        });

        // We get the list of deleted ports after the last process finishes
        const deletedPorts = Object.keys(initialPortsMap).filter(
          id => !remainingPortIdsSet.has(id),
        );

        mutation.mutate(
          {
            updatedIsland,
            updatedPorts,
            deletedPorts,
            newPorts,
          },
          {
            onSuccess: completeSuccess => {
              if (completeSuccess) {
                enqueueSnackbar({
                  variant: 'success',
                  message: t('content.island-and-ports-edition-success'),
                });
              } else {
                enqueueSnackbar({
                  variant: 'warning',
                  message: t(
                    'content.island-and-ports-edition-some-errors-found',
                  ),
                });
              }
              refetchIslandAndPortData();
              callback();
            },
            onError: () => {
              // No API error is displayed as it could be a combination of more than one
              enqueueSnackbar({
                variant: 'error',
                message: t('content.island-and-ports-edition-error'),
              });
              mutation.reset();
            },
          },
        );
      }}
      isPerformingOperation={mutation.isPending}
      isFetching={isFetching}
    />
  );
};

export default EditIslandPage;
